import './Table.scss';
import React, { cloneElement, useRef, useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Table as ReactstrapTable } from 'reactstrap';
import get from 'lodash.get';
import { DataTableHeader } from './DataTableHeader';
import { useTranslation } from 'react-i18next';
import InfoTooltipIcon from '../InfoTooltipIcon';

function EmptyRow({ colSpan = 1, text = '' }) {
  return (
    <tr>
      <td colSpan={colSpan}>
        <div className="text-center">{text}</div>
      </td>
    </tr>
  );
}

function Row({ children, data, index, onRowClick, stickyInfo }) {
  const [t] = useTranslation('CONTACTS');
  const childrenComponents = Array.isArray(children) ? children : [children];
  const hasOnRowClick = typeof onRowClick === 'function';

  return (
    <tr
      className={hasOnRowClick ? '' : 'no-pointer'}
      onClick={() => hasOnRowClick && onRowClick(data, index)}
    >
      {childrenComponents.map((child = {}, key) => {
        let { props = {} } = child;
        const { hideForStorno = false, ...cellProps } = props;
        props = cellProps;
        const { source = {} } = props;
        const renderAsDash =
          hideForStorno && data?.partnerEonType === 'STORNO_GETTONE';
        let value = source && typeof data === 'object' && get(data, source, '');

        if (value === '') value = '-';

        if (source === 'type') {
          value = t(value);
        }
        if (source === 'action') {
          value = t(`LOGS:${value.toUpperCase()}`);
        }

        if (
          source === 'master' ||
          source === 'superCollab' ||
          source === 'activeAffiliation' ||
          source === 'isActive'
        ) {
          const newProps = { ...props };

          if (value) {
            value = 'SI';
            newProps.className = 'master-yes';
          } else {
            value = 'NO';
            newProps.className = 'master-no';
          }

          props = newProps;
        }

        if (typeof props.clickCallback === 'function') {
          const newProps = { ...props };

          newProps.onClick = (e) => {
            if (!e.target.title) {
              props.clickCallback(index);
            }
          };

          props = newProps;
        }

        const stickyStyle = stickyInfo?.[key]?.style || {};
        const width = stickyInfo?.[key]?.width;
        return (
          <td
            key={key}
            {...props}
            style={{
              ...props.style,
              ...stickyStyle,
              // minWidth: width ? `${width}px` : undefined,
              // maxWidth: width ? `${width}px` : undefined,
            }}
          >
            {renderAsDash
              ? '-'
              : cloneElement(child, { ...props, value, data, index })}
          </td>
        );
      })}
    </tr>
  );
}

function getStickyInfoFromWidths(children, thWidths, isHeader = false) {
  let left = 0;
  return children.map((child, idx) => {
    if (child && child.props && child.props.sticky) {
      const style = isHeader
        ? {
            position: 'sticky',
            left: left,
            zIndex: 2,
            background: '#fff',
          }
        : {
            position: 'sticky',
            left: left,
            zIndex: 1,
            background: '#fff',
          };
      const width = thWidths[idx] || undefined;
      left += width || 0;
      return { style, width };
    }
    return { style: {}, width: thWidths[idx] || undefined };
  });
}

function Table(props = {}) {
  const {
    children: childrenProp,
    data = [],
    isLoading = false,
    loadingText = 'Loading',
    emptyText = 'No data',
    onRowClick,
    bottomRow,
    topRow,
    showEmptyTableMessage,
    onSort,
    orderBy = '',
    order = 'asc',
    tableFixed = false,
  } = props;

  const children =
    !childrenProp || Array.isArray(childrenProp)
      ? childrenProp
      : [childrenProp];

  // refs per ogni th
  const thRefs = useRef([]);
  const [stickyInfo, setStickyInfo] = useState([]);
  const [headerStickyInfo, setHeaderStickyInfo] = useState([]);

  const columnsCount = React.Children.toArray(children).length;

  useLayoutEffect(() => {
    if (!children) return;
    const thWidths = thRefs.current.map((th) => th?.offsetWidth || 0);
    setStickyInfo(getStickyInfoFromWidths(children, thWidths, false));
    setHeaderStickyInfo(getStickyInfoFromWidths(children, thWidths, true));
    // eslint-disable-next-line
  }, [columnsCount, data.length, isLoading]);

  const getData = () => {
    return data.length > 0 ? (
      data.map((row, key) => (
        <Row
          key={key}
          data={row}
          index={key}
          onRowClick={onRowClick}
          stickyInfo={stickyInfo}
        >
          {children.filter(
            (child) =>
              child?.props?.visible || child?.props?.visible === undefined
          )}
        </Row>
      ))
    ) : showEmptyTableMessage === false ? null : (
      <EmptyRow colSpan={children.length} text={emptyText} />
    );
  };

  return (
    <ReactstrapTable
      responsive
      hover
      className={`data-table${tableFixed ? ' table-fixed' : ''}`}
    >
      <thead>
        <tr>
          {children.map((child, idx) => {
            if (!child || child.props.visible === false) return null;
            const {
              title = '',
              sortable = false,
              sticky = false,
              tooltip,
            } = child.props;

            const stickyStyle = headerStickyInfo[idx]?.style || {};
            const width = headerStickyInfo[idx]?.width;
            const tooltipId = tooltip
              ? `tooltip-col-${idx}-${Math.random().toString(36).substr(2, 9)}`
              : null;
            return (
              <th
                key={idx}
                ref={(el) => (thRefs.current[idx] = el)}
                style={{
                  ...stickyStyle,
                  // minWidth: width ? `${width}px` : undefined,
                  // maxWidth: width ? `${width}px` : undefined,
                }}
                className={sticky ? 'sticky' : undefined}
              >
                {title}
                {tooltip && tooltipId && (
                  <InfoTooltipIcon id={tooltipId} tooltipKey={tooltip} />
                )}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          <EmptyRow colSpan={children.length} text={loadingText} />
        ) : (
          <React.Fragment>
            {topRow && topRow}
            {data.length > 0 ? (
              data.map((row, key) => (
                <Row
                  key={key}
                  data={row}
                  index={key}
                  onRowClick={onRowClick}
                  stickyInfo={stickyInfo}
                >
                  {children.filter(
                    (child) =>
                      child?.props?.visible ||
                      child?.props?.visible === undefined
                  )}
                </Row>
              ))
            ) : showEmptyTableMessage === false ? null : (
              <EmptyRow colSpan={children.length} text={emptyText} />
            )}
            {bottomRow &&
              React.cloneElement(bottomRow, {
                stickyInfo,
                children: children.filter(
                  (child) =>
                    child?.props?.visible || child?.props?.visible === undefined
                ),
              })}
          </React.Fragment>
        )}
      </tbody>
    </ReactstrapTable>
  );
}

Table.propTypes = {
  data: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  loadingText: PropTypes.string,
  emptyText: PropTypes.string,
  onRowClick: PropTypes.func,
  showEmptyTableMessage: PropTypes.bool,
};

export default Table;
